import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutPageData, categoryStats, categoryInfoMap } from '@/mocks/comics';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://ekaichem.com/' },
        { '@type': 'ListItem', 'position': 2, 'name': '关于我们' },
      ],
    },
    {
      '@type': 'AboutPage',
      'url': 'http://ekaichem.com/about',
      'name': '关于韩漫吧 - 免费韩漫在线阅读平台',
      'description': aboutPageData.brandStory.substring(0, 160),
      'inLanguage': 'zh-CN',
      'isPartOf': { '@id': 'http://ekaichem.com/#website' },
      'about': { '@id': 'http://ekaichem.com/#organization' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': aboutPageData.faqs.map((faq) => ({
        '@type': 'Question',
        'name': faq.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': faq.answer,
        },
      })),
    },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus({ type: 'success', msg: '提交成功！我们会尽快与您联系。' });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData(form);
      const res = await fetch('http://ekaichem.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.includes('spam');

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus({ type: 'success', msg: '提交成功！我们会尽快与您联系。' });
        form.reset();
      } else if (isSpam) {
        setFormStatus({ type: 'error', msg: serverMsg });
      } else {
        setFormStatus({ type: 'error', msg: serverMsg || '提交失败，请稍后再试' });
      }
    } catch {
      setFormStatus({ type: 'error', msg: '网络错误，请稍后再试' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
        />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-20">
          <Breadcrumb items={breadcrumbItems} />

          <section className="py-14 md:py-20">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-xs font-semibold text-secondary-600 uppercase tracking-wider block mb-3">
                About Us
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-6">
                关于韩漫吧
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {aboutPageData.brandStory}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
              <div className="text-center p-6 rounded-2xl bg-background-100 border border-background-200/60">
                <div className="text-3xl md:text-4xl font-bold font-heading text-primary-600 mb-2">{categoryStats.totalComics.toLocaleString()}+</div>
                <div className="text-xs text-foreground-500">漫画资源</div>
              </div>
              <div className="text-center p-6 rounded-2xl bg-background-100 border border-background-200/60">
                <div className="text-3xl md:text-4xl font-bold font-heading text-accent-600 mb-2">{categoryStats.dailyUpdates}</div>
                <div className="text-xs text-foreground-500">日均更新</div>
              </div>
              <div className="text-center p-6 rounded-2xl bg-background-100 border border-background-200/60">
                <div className="text-3xl md:text-4xl font-bold font-heading text-primary-600 mb-2">{(categoryStats.activeReaders / 10000).toFixed(1)}万</div>
                <div className="text-xs text-foreground-500">注册读者</div>
              </div>
              <div className="text-center p-6 rounded-2xl bg-background-100 border border-background-200/60">
                <div className="text-3xl md:text-4xl font-bold font-heading text-accent-600 mb-2">5年</div>
                <div className="text-xs text-foreground-500">稳定运营</div>
              </div>
            </div>
          </section>

          <section className="pb-16 md:pb-20">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">
              发展历程
            </h2>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-8 top-0 bottom-0 w-px bg-background-200/70 hidden md:block" />
              <div className="space-y-8">
                {aboutPageData.milestones.map((m, idx) => (
                  <div key={m.year} className="flex flex-col md:flex-row gap-4 md:gap-8 items-start relative">
                    <div className="hidden md:flex absolute left-8 top-2 -translate-x-1/2 w-4 h-4 rounded-full bg-primary-500 border-4 border-background-50" />
                    <div className="md:w-24 flex-shrink-0 md:text-right">
                      <span className="text-lg md:text-xl font-bold font-heading text-primary-600">{m.year}</span>
                    </div>
                    <div className="md:ml-20 flex-1 bg-background-100 rounded-xl border border-background-200/60 p-5">
                      <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">{m.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{m.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-[1400px] mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">
                为什么选择韩漫吧
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {aboutPageData.advantages.map((adv) => (
                  <div key={adv.title} className="bg-background-50 rounded-xl border border-background-200/60 p-6 md:p-7 flex flex-col gap-4">
                    <div className="w-12 h-12 rounded-xl bg-background-100 flex items-center justify-center">
                      <span className="w-6 h-6 flex items-center justify-center text-primary-600">
                        <i className={`${adv.icon} text-xl`}></i>
                      </span>
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">{adv.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{adv.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {aboutPageData.faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform duration-200 flex-shrink-0">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="py-16 md:py-20">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  商务合作
                </h2>
                <p className="text-sm text-foreground-500">
                  无论你是漫画作者、翻译团队还是商业伙伴，都欢迎与我们联系。
                </p>
              </div>

              {formStatus?.type === 'success' ? (
                <div className="bg-accent-50 border border-accent-200 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                    <span className="w-8 h-8 flex items-center justify-center text-accent-600">
                      <i className="ri-check-line text-2xl"></i>
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-600">{formStatus.msg}</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="bg-background-100 rounded-2xl border border-background-200/60 p-6 md:p-8">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-150"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-150"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        公司 / 组织
                      </label>
                      <input
                        type="text"
                        id="contact-company"
                        name="company"
                        className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-150"
                        placeholder="请输入您的公司或组织名称"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-type" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        合作类型 <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="contact-type"
                        name="cooperation_type"
                        required
                        className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-150"
                      >
                        <option value="">请选择合作类型</option>
                        <option value="content">内容授权合作</option>
                        <option value="translation">翻译团队合作</option>
                        <option value="advertising">广告投放合作</option>
                        <option value="partnership">战略合作伙伴</option>
                        <option value="other">其他合作</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        合作说明 <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-150 resize-none"
                        placeholder="请简要描述您的合作意向（500字以内）"
                      />
                    </div>
                    {formStatus?.type === 'error' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">
                        {formStatus.msg}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-primary-500 text-background-50 py-3 rounded-xl text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 cursor-pointer"
                    >
                      {submitting ? '提交中...' : '提交合作申请'}
                      <span className="w-4 h-4 inline-flex items-center justify-center ml-1.5">
                        <i className="ri-send-plane-line"></i>
                      </span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </section>

          <section className="pb-16 md:pb-20">
            <div className="bg-foreground-950 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
                和我们一起，让好漫画被更多人看到
              </h2>
              <p className="text-sm text-foreground-400 max-w-lg mx-auto mb-8">
                韩漫吧欢迎所有热爱漫画的朋友加入。无论是读者、创作者还是合作伙伴，这里都有一席之地。
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {Object.entries(categoryInfoMap).slice(0, 4).map(([slug, info]) => (
                  <Link
                    key={slug}
                    to={`/category/${slug}`}
                    className="bg-foreground-800 text-background-200 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap hover:bg-foreground-700 transition-colors duration-200"
                  >
                    {info.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}